import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../lib/auth';
import { toast } from 'sonner';
import {
  StickyNote, Plus, X, Archive, ArchiveRestore, Trash2,
  Clock, Pin, PinOff, GripHorizontal, Minimize2, ExternalLink, RotateCcw,
  Bell, BellOff,
} from 'lucide-react';

const NOTE_COLORS = [
  { key: 'yellow', bg: 'bg-amber-50', border: 'border-amber-200', header: 'bg-amber-100', text: 'text-amber-900', fab: 'from-amber-400 to-amber-500', pin: '#fef3c7' },
  { key: 'blue', bg: 'bg-sky-50', border: 'border-sky-200', header: 'bg-sky-100', text: 'text-sky-900', fab: 'from-sky-400 to-sky-500', pin: '#e0f2fe' },
  { key: 'green', bg: 'bg-emerald-50', border: 'border-emerald-200', header: 'bg-emerald-100', text: 'text-emerald-900', fab: 'from-emerald-400 to-emerald-500', pin: '#d1fae5' },
  { key: 'pink', bg: 'bg-rose-50', border: 'border-rose-200', header: 'bg-rose-100', text: 'text-rose-900', fab: 'from-rose-400 to-rose-500', pin: '#ffe4e6' },
  { key: 'purple', bg: 'bg-violet-50', border: 'border-violet-200', header: 'bg-violet-100', text: 'text-violet-900', fab: 'from-violet-400 to-violet-500', pin: '#ede9fe' },
];

const getColor = (key) => NOTE_COLORS.find(c => c.key === key) || NOTE_COLORS[0];

const FAB_POS_KEY = 'sticky-fab-position';
const PANEL_POS_KEY = 'sticky-panel-position';
const PINNED_KEY = 'sticky-pinned-notes';

// Quick-set reminder presets: each returns a Date relative to "now" at the
// moment of the click. "Yarın sabah" = tomorrow 09:00 local time.
const REMINDER_PRESETS = [
  { key: '1h',  label: '1 saat sonra',   compute: () => new Date(Date.now() + 60 * 60 * 1000) },
  { key: '3h',  label: '3 saat sonra',   compute: () => new Date(Date.now() + 3 * 60 * 60 * 1000) },
  { key: 'tom', label: 'Yarın sabah',    compute: () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); return d; } },
  { key: '1w',  label: '1 hafta sonra',  compute: () => { const d = new Date(); d.setDate(d.getDate() + 7); d.setHours(9, 0, 0, 0); return d; } },
  { key: 'mon', label: 'Pazartesi 9:00', compute: () => {
      const d = new Date();
      const day = d.getDay(); // 0=Sun..6=Sat
      const add = day === 1 ? 7 : ((8 - day) % 7) || 7;
      d.setDate(d.getDate() + add);
      d.setHours(9, 0, 0, 0);
      return d;
    }
  },
];

export default function StickyNotesWidget() {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [pinnedNoteIds, setPinnedNoteIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(PINNED_KEY) || '[]'); } catch { return []; }
  });
  const [pinnedPositions, setPinnedPositions] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sticky-pinned-positions') || '{}'); } catch { return {}; }
  });
  const panelRef = useRef(null);

  // FAB position
  const [fabPos, setFabPos] = useState(() => {
    try { const s = localStorage.getItem(FAB_POS_KEY); if (s) return JSON.parse(s); } catch {}
    return { right: 24, bottom: 24 };
  });
  const [fabDragging, setFabDragging] = useState(false);
  const fabDragRef = useRef({ startX: 0, startY: 0, startRight: 0, startBottom: 0, moved: false });

  // Panel position
  const [panelPos, setPanelPos] = useState(() => {
    try { const s = localStorage.getItem(PANEL_POS_KEY); if (s) return JSON.parse(s); } catch {}
    return null;
  });
  const [panelDragging, setPanelDragging] = useState(false);
  const panelDragRef = useRef({ startX: 0, startY: 0, startLeft: 0, startTop: 0 });

  // Document Picture-in-Picture (masaüstünde yüzen pencere)
  const [pipWindow, setPipWindow] = useState(null);
  const pipSupported = typeof window !== 'undefined' && 'documentPictureInPicture' in window;

  const openInPipWindow = useCallback(async () => {
    if (!pipSupported) {
      toast.error('Bu tarayıcı yüzen pencereyi desteklemiyor. Chrome/Edge 116+ gerekli.');
      return;
    }
    try {
      const pip = await window.documentPictureInPicture.requestWindow({
        width: 380,
        height: 560,
      });
      // Copy all <style> and <link rel="stylesheet"> into the PiP document so
      // Tailwind classes work. Without this, the floating window renders
      // un-styled markup.
      [...document.styleSheets].forEach(ss => {
        try {
          const cssRules = [...ss.cssRules].map(r => r.cssText).join('');
          const style = pip.document.createElement('style');
          style.textContent = cssRules;
          pip.document.head.appendChild(style);
        } catch {
          // Cross-origin or CSSOM-restricted sheets fall back to linking.
          if (ss.href) {
            const link = pip.document.createElement('link');
            link.rel = 'stylesheet';
            link.href = ss.href;
            pip.document.head.appendChild(link);
          }
        }
      });
      pip.document.body.style.margin = '0';
      pip.document.body.style.background = 'transparent';
      pip.document.title = 'Notlarım';
      pip.addEventListener('pagehide', () => setPipWindow(null));
      setPipWindow(pip);
      setOpen(false); // main-page panel kapansın; PiP bayraklı pencere açılıyor
    } catch (e) {
      toast.error('Yüzen pencere açılamadı: ' + (e.message || ''));
    }
  }, [pipSupported]);

  const closePipWindow = useCallback(() => {
    try { pipWindow?.close(); } catch { /* ignore */ }
    setPipWindow(null);
  }, [pipWindow]);

  const fetchNotes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/sticky-notes?include_archived=${showArchived}`);
      if (res.data?.success) setNotes(res.data.data || []);
    } catch {} finally { setLoading(false); }
  }, [showArchived]);

  useEffect(() => { if (open || pinnedNoteIds.length > 0) fetchNotes(); }, [open, fetchNotes, pinnedNoteIds.length]);

  // Save pinned IDs
  useEffect(() => {
    localStorage.setItem(PINNED_KEY, JSON.stringify(pinnedNoteIds));
  }, [pinnedNoteIds]);
  useEffect(() => {
    localStorage.setItem('sticky-pinned-positions', JSON.stringify(pinnedPositions));
  }, [pinnedPositions]);

  const addNote = async () => {
    try {
      const res = await api.post('/sticky-notes', { content: '', color: 'yellow' });
      if (res.data?.success) {
        setNotes(prev => [res.data.data, ...prev]);
        setEditingId(res.data.data.id);
      }
    } catch { toast.error('Not eklenemedi'); }
  };

  const updateNote = async (id, data) => {
    try {
      await api.put(`/sticky-notes/${id}`, data);
      setNotes(prev => prev.map(n => n.id === id ? { ...n, ...data, updated_at: new Date().toISOString() } : n));
    } catch { toast.error('Güncelleme hatası'); }
  };

  const archiveNote = async (id) => {
    await updateNote(id, { archived: true });
    setNotes(prev => prev.filter(n => n.id !== id));
    setPinnedNoteIds(prev => prev.filter(pid => pid !== id));
    toast.success('Arşivlendi');
  };

  const unarchiveNote = async (id) => {
    await updateNote(id, { archived: false });
    fetchNotes();
    toast.success('Geri alındı');
  };

  const deleteNote = async (id) => {
    try {
      await api.delete(`/sticky-notes/${id}`);
      setNotes(prev => prev.filter(n => n.id !== id));
      setPinnedNoteIds(prev => prev.filter(pid => pid !== id));
      toast.success('Kalıcı olarak silindi');
    } catch { toast.error('Silme hatası'); }
  };

  const togglePin = (noteId) => {
    setPinnedNoteIds(prev => {
      if (prev.includes(noteId)) return prev.filter(id => id !== noteId);
      return [...prev, noteId];
    });
  };

  const updatePinnedPosition = (noteId, pos) => {
    setPinnedPositions(prev => ({ ...prev, [noteId]: pos }));
  };

  // Close panel on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && !e.target.closest('[data-sticky-fab]') && !e.target.closest('[data-pinned-note]')) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Listen for in-app reminder fires broadcast over WebSocket (dispatched
  // by useRealtimeMessages). Shows a toast + refreshes notes so the chip
  // colour flips immediately to the "past" state.
  useEffect(() => {
    const handler = (e) => {
      const { content, note_id } = e.detail || {};
      toast.info(`Hatırlatıcı: ${content || 'zamanı geldi'}`, {
        duration: 10000,
        action: {
          label: 'Aç',
          onClick: () => setOpen(true),
        },
      });
      // Refresh so the UI reflects reminder_sent=true
      fetchNotes?.();
      // Auto-open the panel once on reminder fire for visibility
      setOpen(true);
      // Note: id kept in detail for future "jump to note" features
      void note_id;
    };
    window.addEventListener('sticky-reminder', handler);
    return () => window.removeEventListener('sticky-reminder', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ==================== FAB DRAG ====================
  const handleFabDragStart = (e) => {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    fabDragRef.current = { startX: clientX, startY: clientY, startRight: fabPos.right, startBottom: fabPos.bottom, moved: false };
    setFabDragging(true);
  };

  useEffect(() => {
    if (!fabDragging) return;
    const handleMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const dx = fabDragRef.current.startX - clientX;
      const dy = fabDragRef.current.startY - clientY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) fabDragRef.current.moved = true;
      setFabPos({
        right: Math.max(8, Math.min(window.innerWidth - 60, fabDragRef.current.startRight + dx)),
        bottom: Math.max(8, Math.min(window.innerHeight - 60, fabDragRef.current.startBottom + dy))
      });
    };
    const handleEnd = () => {
      setFabDragging(false);
      localStorage.setItem(FAB_POS_KEY, JSON.stringify(fabPos));
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('touchend', handleEnd);
    return () => { window.removeEventListener('mousemove', handleMove); window.removeEventListener('mouseup', handleEnd); window.removeEventListener('touchmove', handleMove); window.removeEventListener('touchend', handleEnd); };
  }, [fabDragging, fabPos]);

  const handleFabClick = () => { if (!fabDragRef.current.moved) setOpen(!open); fabDragRef.current.moved = false; };

  // Reset FAB and panel positions to defaults (bottom-right corner). Useful
  // when the user drags the widget to an awkward spot and wants to recover.
  const resetPositions = () => {
    setFabPos({ right: 24, bottom: 24 });
    setPanelPos(null);
    try {
      localStorage.removeItem(FAB_POS_KEY);
      localStorage.removeItem(PANEL_POS_KEY);
    } catch {}
    toast.success('Not paneli varsayılan konuma alındı');
  };

  // ==================== PANEL DRAG ====================
  const handlePanelDragStart = (e) => {
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Initialize panel position from current rect if not set
    const currentLeft = panelPos?.left ?? rect.left;
    const currentTop = panelPos?.top ?? rect.top;
    panelDragRef.current = { startX: clientX, startY: clientY, startLeft: currentLeft, startTop: currentTop };
    setPanelDragging(true);
  };

  useEffect(() => {
    if (!panelDragging) return;
    const handleMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const dx = clientX - panelDragRef.current.startX;
      const dy = clientY - panelDragRef.current.startY;
      const newLeft = Math.max(0, Math.min(window.innerWidth - 340, panelDragRef.current.startLeft + dx));
      const newTop = Math.max(0, Math.min(window.innerHeight - 100, panelDragRef.current.startTop + dy));
      setPanelPos({ left: newLeft, top: newTop });
    };
    const handleEnd = () => {
      setPanelDragging(false);
      if (panelPos) localStorage.setItem(PANEL_POS_KEY, JSON.stringify(panelPos));
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('touchend', handleEnd);
    return () => { window.removeEventListener('mousemove', handleMove); window.removeEventListener('mouseup', handleEnd); window.removeEventListener('touchmove', handleMove); window.removeEventListener('touchend', handleEnd); };
  }, [panelDragging, panelPos]);

  const activeNotes = notes.filter(n => !n.archived);
  const archivedNotes = notes.filter(n => n.archived);
  const pinnedNotes = notes.filter(n => pinnedNoteIds.includes(n.id) && !n.archived);

  // Panel style: use saved drag position if present; otherwise compute an
  // anchor direction that flips based on which quadrant the FAB is in so the
  // panel always opens toward the viewport center (bug fix: FAB moved to
  // top-right/top-left used to open the panel off-screen).
  const panelStyle = useMemo(() => {
    if (panelPos) {
      return { position: 'fixed', left: panelPos.left, top: panelPos.top, zIndex: 50 };
    }
    const fabSize = 48; // w-12 h-12
    const gap = 8;
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
    // fabPos is stored as { right, bottom } offsets from viewport edges.
    const fabRightPx = fabPos.right;
    const fabBottomPx = fabPos.bottom;
    const fabLeftPx = vw - fabRightPx - fabSize;   // distance from viewport left edge to FAB's left
    const fabTopPx = vh - fabBottomPx - fabSize;   // distance from viewport top edge to FAB's top

    const spaceAbove = fabTopPx;
    const spaceBelow = fabBottomPx;
    const openUpward = spaceAbove >= spaceBelow;

    const spaceLeft = fabLeftPx;
    const spaceRight = fabRightPx;
    const anchorRight = spaceRight <= spaceLeft; // FAB closer to right edge → anchor panel's right

    const style = { position: 'fixed', zIndex: 50 };
    if (openUpward) {
      // panel bottom sits just above FAB top
      style.bottom = fabBottomPx + fabSize + gap;
    } else {
      // panel top sits just below FAB bottom
      style.top = fabTopPx + fabSize + gap;
    }
    if (anchorRight) {
      style.right = fabRightPx;
    } else {
      style.left = fabLeftPx;
    }
    return style;
  }, [panelPos, fabPos]);

  return (
    <>
      {/* Pinned notes on desktop */}
      {pinnedNotes.map(note => (
        <PinnedNote
          key={note.id}
          note={note}
          position={pinnedPositions[note.id]}
          onPositionChange={(pos) => updatePinnedPosition(note.id, pos)}
          onUnpin={() => togglePin(note.id)}
          onUpdate={updateNote}
          onArchive={archiveNote}
          onDelete={deleteNote}
        />
      ))}

      {/* FAB Button */}
      <button
        data-sticky-fab
        onMouseDown={handleFabDragStart}
        onTouchStart={handleFabDragStart}
        onClick={handleFabClick}
        className={`fixed z-50 w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-colors duration-200 hover:shadow-xl ${fabDragging ? 'cursor-grabbing scale-110' : 'cursor-grab'} ${open ? 'bg-slate-700 text-white' : 'bg-gradient-to-br from-amber-400 to-amber-500 text-white'}`}
        style={{ right: fabPos.right, bottom: fabPos.bottom, transition: fabDragging ? 'none' : 'box-shadow 0.2s' }}
        data-testid="sticky-notes-fab"
      >
        {open ? <X className="h-5 w-5" /> : <StickyNote className="h-5 w-5" />}
        {!open && activeNotes.length > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {activeNotes.length}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          ref={panelRef}
          className="w-[340px] max-h-[70vh] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 flex flex-col overflow-hidden"
          style={panelStyle}
          data-testid="sticky-notes-panel"
        >
          {/* Draggable header */}
          <div
            className={`flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800 select-none ${panelDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            onMouseDown={handlePanelDragStart}
            onTouchStart={handlePanelDragStart}
          >
            <div className="flex items-center gap-2">
              <GripHorizontal className="h-3.5 w-3.5 text-slate-300 dark:text-slate-600" />
              <StickyNote className="h-4 w-4 text-amber-500" />
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Notlarım</span>
              <span className="text-[10px] bg-slate-100 dark:bg-slate-800 text-slate-500 px-1.5 py-0.5 rounded-full">{activeNotes.length}</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={resetPositions}
                className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                title="Varsayılan konuma sıfırla"
                data-testid="reset-sticky-position"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
              {pipSupported && (
                <button
                  onClick={openInPipWindow}
                  className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-indigo-600 transition-colors"
                  title="Masaüstünde yüzen pencere olarak aç"
                  data-testid="open-pip"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                onClick={() => setShowArchived(!showArchived)}
                className={`p-1.5 rounded-lg transition-colors ${showArchived ? 'bg-amber-100 text-amber-600' : 'text-slate-400 hover:bg-slate-100'}`}
                title={showArchived ? 'Arşivi Gizle' : 'Arşivi Göster'}
                data-testid="toggle-archive"
              >
                <Archive className="h-3.5 w-3.5" />
              </button>
              <button onClick={addNote} className="p-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors" data-testid="add-sticky-note">
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Notes list */}
          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            {loading && notes.length === 0 && <p className="text-center text-xs text-slate-400 py-8">Yükleniyor...</p>}
            {!loading && activeNotes.length === 0 && !showArchived && (
              <div className="text-center py-8">
                <StickyNote className="h-8 w-8 mx-auto text-slate-200 mb-2" />
                <p className="text-xs text-slate-400">Henüz not yok</p>
                <p className="text-[10px] text-slate-300 mt-0.5">+ butonuyla hızlıca not ekleyin</p>
              </div>
            )}
            {activeNotes.map(note => (
              <NoteItem
                key={note.id}
                note={note}
                isEditing={editingId === note.id}
                isPinned={pinnedNoteIds.includes(note.id)}
                onStartEdit={() => setEditingId(note.id)}
                onStopEdit={() => setEditingId(null)}
                onUpdate={updateNote}
                onArchive={archiveNote}
                onDelete={deleteNote}
                onTogglePin={() => togglePin(note.id)}
              />
            ))}
            {showArchived && archivedNotes.length > 0 && (
              <div className="pt-2 mt-2 border-t border-slate-100 dark:border-slate-800">
                <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider px-1 mb-1.5 flex items-center gap-1">
                  <Archive className="h-3 w-3" /> Arşiv ({archivedNotes.length})
                </p>
                {archivedNotes.map(note => (
                  <NoteItem key={note.id} note={note} isArchived onUnarchive={unarchiveNote} onDelete={deleteNote} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Picture-in-Picture floating panel (masaüstü) */}
      {pipWindow && createPortal(
        <div className="w-full h-screen bg-white dark:bg-slate-900 flex flex-col overflow-hidden border-t-4 border-amber-400">
          <div className="flex items-center justify-between px-3 py-2 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-900/40">
            <div className="flex items-center gap-2">
              <StickyNote className="h-4 w-4 text-amber-500" />
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Notlarım</span>
              <span className="text-[10px] bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded-full">{activeNotes.length}</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setShowArchived(!showArchived)}
                className={`p-1.5 rounded-lg transition-colors ${showArchived ? 'bg-amber-200 text-amber-700' : 'text-slate-400 hover:bg-amber-100'}`}
                title={showArchived ? 'Arşivi Gizle' : 'Arşivi Göster'}
              >
                <Archive className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={addNote}
                className="p-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors"
                title="Yeni not"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={closePipWindow}
                className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-red-500 transition-colors"
                title="Yüzen pencereyi kapat"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            {activeNotes.length === 0 && !showArchived && (
              <div className="text-center py-8">
                <StickyNote className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-400">Henüz notunuz yok</p>
                <button onClick={addNote} className="mt-2 text-xs text-amber-600 hover:text-amber-700 underline">İlk notu oluşturun</button>
              </div>
            )}
            {activeNotes.map(note => (
              <NoteItem
                key={note.id}
                note={note}
                isEditing={editingId === note.id}
                isPinned={pinnedNoteIds.includes(note.id)}
                onStartEdit={() => setEditingId(note.id)}
                onStopEdit={() => setEditingId(null)}
                onUpdate={updateNote}
                onArchive={archiveNote}
                onDelete={deleteNote}
                onTogglePin={() => togglePin(note.id)}
              />
            ))}
            {showArchived && archivedNotes.length > 0 && (
              <div className="pt-2 mt-2 border-t border-slate-100 dark:border-slate-800">
                <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider px-1 mb-1.5 flex items-center gap-1">
                  <Archive className="h-3 w-3" /> Arşiv ({archivedNotes.length})
                </p>
                {archivedNotes.map(note => (
                  <NoteItem key={note.id} note={note} isArchived onUnarchive={unarchiveNote} onDelete={deleteNote} />
                ))}
              </div>
            )}
          </div>
        </div>,
        pipWindow.document.body,
      )}
    </>
  );
}

// ==================== NOTE ITEM (in panel) ====================
function NoteItem({ note, isEditing, isArchived, isPinned, onStartEdit, onStopEdit, onUpdate, onArchive, onUnarchive, onDelete, onTogglePin }) {
  const color = getColor(note.color);
  const textRef = useRef(null);
  const debounceRef = useRef(null);
  const [showReminder, setShowReminder] = useState(false);
  // datetime-local input works in local timezone; convert stored UTC ISO → local string for editing.
  const isoToLocalInput = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      const tz = d.getTimezoneOffset();
      const local = new Date(d.getTime() - tz * 60000);
      return local.toISOString().slice(0, 16);
    } catch { return ''; }
  };
  const [reminderDraft, setReminderDraft] = useState(() => isoToLocalInput(note.remind_at));

  useEffect(() => {
    if (isEditing && textRef.current) {
      textRef.current.focus();
      const el = textRef.current;
      el.selectionStart = el.selectionEnd = el.value.length;
    }
  }, [isEditing]);

  useEffect(() => { setReminderDraft(isoToLocalInput(note.remind_at)); }, [note.remind_at]);

  const handleChange = (value) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onUpdate?.(note.id, { content: value }), 500);
  };

  const saveReminder = () => {
    if (!reminderDraft) {
      onUpdate?.(note.id, { remind_at: '' });
    } else {
      // Convert local datetime → UTC ISO
      const iso = new Date(reminderDraft).toISOString();
      onUpdate?.(note.id, { remind_at: iso });
    }
    setShowReminder(false);
  };

  const clearReminder = () => {
    setReminderDraft('');
    onUpdate?.(note.id, { remind_at: '' });
    setShowReminder(false);
  };

  const hasReminder = !!note.remind_at;
  const reminderPast = hasReminder && new Date(note.remind_at).getTime() <= Date.now();

  if (isArchived) {
    return (
      <div className={`${color.bg} ${color.border} border rounded-lg p-2.5 opacity-60 group`} data-testid={`archived-note-${note.id}`}>
        <p className="text-xs text-slate-500 whitespace-pre-wrap line-clamp-2">{note.content || '(boş)'}</p>
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-[9px] text-slate-400">{new Date(note.updated_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
          <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={() => onUnarchive?.(note.id)} className="p-1 rounded hover:bg-white/60" title="Geri Al"><ArchiveRestore className="h-3 w-3 text-emerald-500" /></button>
            <button onClick={() => onDelete?.(note.id)} className="p-1 rounded hover:bg-white/60" title="Kalıcı Sil"><Trash2 className="h-3 w-3 text-red-400" /></button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${color.bg} ${color.border} border rounded-lg overflow-hidden group transition-shadow hover:shadow-md`} data-testid={`sticky-note-${note.id}`}>
      <div className={`${color.header} px-2.5 py-1.5 flex items-center justify-between`}>
        <div className="flex gap-1">
          {NOTE_COLORS.map(c => (
            <button key={c.key} onClick={() => onUpdate?.(note.id, { color: c.key })}
              className={`w-4 h-4 rounded-full border-2 transition-transform hover:scale-125 ${c.key === note.color ? 'border-slate-600 scale-110' : 'border-white/60'}`}
              style={{ background: `linear-gradient(135deg, var(--tw-gradient-stops))` }}>
              <span className={`block w-full h-full rounded-full bg-gradient-to-br ${c.fab}`} />
            </button>
          ))}
        </div>
        <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); setShowReminder(v => !v); }}
            className={`p-0.5 rounded hover:bg-white/60 ${hasReminder ? 'opacity-100' : ''}`}
            title={hasReminder ? 'Hatırlatıcıyı düzenle' : 'Hatırlatıcı ekle'}
            data-testid={`reminder-btn-${note.id}`}
          >
            <Bell className={`h-3 w-3 ${hasReminder ? (reminderPast ? 'text-red-500' : 'text-indigo-500') : 'text-slate-500'}`} />
          </button>
          <button onClick={() => onTogglePin?.()} className={`p-0.5 rounded hover:bg-white/60 ${isPinned ? 'opacity-100' : ''}`} title={isPinned ? 'Sabitlemeden Kaldır' : 'Masaüstüne Sabitle'}>
            {isPinned ? <PinOff className="h-3 w-3 text-blue-500" /> : <Pin className="h-3 w-3 text-slate-500" />}
          </button>
          <button onClick={() => onArchive?.(note.id)} className="p-0.5 rounded hover:bg-white/60" title="Arşivle"><Archive className="h-3 w-3 text-slate-500" /></button>
          <button onClick={() => onDelete?.(note.id)} className="p-0.5 rounded hover:bg-white/60" title="Sil"><Trash2 className="h-3 w-3 text-red-400" /></button>
        </div>
      </div>
      {showReminder && (
        <div className="px-2.5 py-2 bg-white/80 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-700" onClick={(e) => e.stopPropagation()}>
          <label className="text-[10px] text-slate-500 uppercase tracking-wide">Hatırlatma zamanı</label>
          {/* Quick presets — one-click relative offsets that also auto-save */}
          <div className="flex flex-wrap gap-1 mt-1.5">
            {REMINDER_PRESETS.map(p => (
              <button
                key={p.key}
                type="button"
                onClick={() => {
                  const iso = p.compute().toISOString();
                  onUpdate?.(note.id, { remind_at: iso });
                  setShowReminder(false);
                }}
                className="px-2 py-0.5 text-[11px] rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-700 transition-colors"
                data-testid={`reminder-preset-${p.key}-${note.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <input
            type="datetime-local"
            value={reminderDraft}
            onChange={(e) => setReminderDraft(e.target.value)}
            className="w-full mt-1.5 px-2 py-1 text-xs rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
            data-testid={`reminder-input-${note.id}`}
          />
          <div className="flex items-center justify-end gap-1 mt-1.5">
            {hasReminder && (
              <button onClick={clearReminder} className="px-2 py-0.5 text-[11px] rounded text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700" data-testid={`reminder-clear-${note.id}`}>
                <BellOff className="h-3 w-3 inline mr-0.5" /> Kaldır
              </button>
            )}
            <button onClick={() => setShowReminder(false)} className="px-2 py-0.5 text-[11px] rounded text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700">Vazgeç</button>
            <button onClick={saveReminder} className="px-2 py-0.5 text-[11px] rounded bg-indigo-500 text-white hover:bg-indigo-600" data-testid={`reminder-save-${note.id}`}>Kaydet</button>
          </div>
        </div>
      )}
      <div className="px-2.5 py-2" onClick={() => !isEditing && onStartEdit?.()}>
        {isEditing ? (
          <textarea ref={textRef} defaultValue={note.content} onChange={e => handleChange(e.target.value)} onBlur={() => onStopEdit?.()}
            className={`w-full bg-transparent border-none outline-none resize-none text-sm ${color.text} placeholder:opacity-40 min-h-[60px]`} placeholder="Not yazın..." />
        ) : (
          <p className={`text-sm ${color.text} whitespace-pre-wrap min-h-[24px] cursor-text ${!note.content ? 'opacity-40 italic' : ''}`}>
            {note.content || 'Tıklayarak yazmaya başlayın...'}
          </p>
        )}
      </div>
      <div className="px-2.5 pb-1.5 flex items-center justify-between gap-1">
        <span className="text-[9px] text-slate-400 flex items-center gap-0.5">
          <Clock className="h-2.5 w-2.5" />
          {new Date(note.updated_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </span>
        <div className="flex items-center gap-1">
          {hasReminder && (
            <span
              className={`text-[9px] inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full border ${reminderPast ? 'bg-red-50 text-red-600 border-red-200' : 'bg-indigo-50 text-indigo-600 border-indigo-200'}`}
              title={new Date(note.remind_at).toLocaleString('tr-TR')}
              data-testid={`reminder-badge-${note.id}`}
            >
              <Bell className="h-2.5 w-2.5" />
              {new Date(note.remind_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          {isPinned && <Pin className="h-2.5 w-2.5 text-blue-400" />}
        </div>
      </div>
    </div>
  );
}

// ==================== PINNED NOTE (on desktop) ====================
function PinnedNote({ note, position, onPositionChange, onUnpin, onUpdate, onArchive, onDelete }) {
  const color = getColor(note.color);
  const [dragging, setDragging] = useState(false);
  const [editing, setEditing] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, startLeft: 0, startTop: 0 });
  const textRef = useRef(null);
  const debounceRef = useRef(null);
  const [pos, setPos] = useState(position || { left: 100 + Math.random() * 300, top: 100 + Math.random() * 200 });

  useEffect(() => { if (position) setPos(position); }, [position]);

  const handleDragStart = (e) => {
    if (editing) return;
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragRef.current = { startX: clientX, startY: clientY, startLeft: pos.left, startTop: pos.top };
    setDragging(true);
  };

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const newLeft = Math.max(0, Math.min(window.innerWidth - 220, dragRef.current.startLeft + (clientX - dragRef.current.startX)));
      const newTop = Math.max(0, Math.min(window.innerHeight - 100, dragRef.current.startTop + (clientY - dragRef.current.startY)));
      setPos({ left: newLeft, top: newTop });
    };
    const handleEnd = () => {
      setDragging(false);
      onPositionChange?.(pos);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('touchend', handleEnd);
    return () => { window.removeEventListener('mousemove', handleMove); window.removeEventListener('mouseup', handleEnd); window.removeEventListener('touchmove', handleMove); window.removeEventListener('touchend', handleEnd); };
  }, [dragging, pos, onPositionChange]);

  useEffect(() => {
    if (editing && textRef.current) {
      textRef.current.focus();
      const el = textRef.current;
      el.selectionStart = el.selectionEnd = el.value.length;
    }
  }, [editing]);

  const handleChange = (value) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onUpdate?.(note.id, { content: value }), 500);
  };

  return (
    <div
      data-pinned-note
      className={`fixed z-40 w-52 rounded-xl shadow-xl border-2 ${color.border} overflow-hidden transition-shadow ${dragging ? 'shadow-2xl scale-[1.02]' : 'hover:shadow-lg'}`}
      style={{ left: pos.left, top: pos.top, backgroundColor: color.pin, transition: dragging ? 'none' : 'box-shadow 0.2s, transform 0.15s' }}
      data-testid={`pinned-note-${note.id}`}
    >
      {/* Drag handle + actions */}
      <div
        className={`flex items-center justify-between px-2.5 py-1.5 ${color.header} ${dragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      >
        <div className="flex items-center gap-1">
          <GripHorizontal className="h-3 w-3 text-slate-400" />
          <StickyNote className="h-3 w-3 text-slate-500" />
        </div>
        <div className="flex gap-0.5">
          <button onClick={onUnpin} className="p-0.5 rounded hover:bg-white/60" title="Sabitlemeden Kaldır"><Minimize2 className="h-3 w-3 text-blue-500" /></button>
          <button onClick={() => onArchive?.(note.id)} className="p-0.5 rounded hover:bg-white/60" title="Arşivle"><Archive className="h-3 w-3 text-slate-500" /></button>
          <button onClick={() => onDelete?.(note.id)} className="p-0.5 rounded hover:bg-white/60" title="Sil"><Trash2 className="h-3 w-3 text-red-400" /></button>
        </div>
      </div>
      {/* Content */}
      <div className="px-2.5 py-2" onClick={() => !editing && setEditing(true)}>
        {editing ? (
          <textarea
            ref={textRef}
            defaultValue={note.content}
            onChange={e => handleChange(e.target.value)}
            onBlur={() => setEditing(false)}
            className={`w-full bg-transparent border-none outline-none resize-none text-xs ${color.text} placeholder:opacity-40 min-h-[50px]`}
            placeholder="Not yazın..."
          />
        ) : (
          <p className={`text-xs ${color.text} whitespace-pre-wrap min-h-[20px] cursor-text leading-relaxed ${!note.content ? 'opacity-40 italic' : ''}`}>
            {note.content || 'Tıklayarak yazın...'}
          </p>
        )}
      </div>
    </div>
  );
}
